import type { ResponseError } from "app/services/fetchService";

import { SALESLOFT_FORBIDDEN_STATUS } from "../constants/salesloftConstants";
import { useSalesloftConnectionProbeQuery } from "../queries/useSalesloftConnectionProbeQuery";

import { useSalesloftConnect } from "./useSalesloftConnect";

type UseSalesloftConnectionStatusParams = {
    enabled?: boolean;
    /** Called after the user reconnects Salesloft and the probe succeeds again. */
    onReconnected?: () => void;
};

/**
 * Connection check for screens that don't list cadences themselves: a 403 from the probe means the
 * Salesloft connection expired (`isReconnectRequired`), and `openSalesloftConnect` opens the OAuth popup
 * then re-probes — invoking `onReconnected` once it succeeds.
 */
export const useSalesloftConnectionStatus = ({
    enabled = true,
    onReconnected,
}: UseSalesloftConnectionStatusParams = {}) => {
    const { error, refetch } = useSalesloftConnectionProbeQuery({ enabled });

    const isReconnectRequired = (error as ResponseError | null)?.status === SALESLOFT_FORBIDDEN_STATUS;

    const { openSalesloftConnect } = useSalesloftConnect({
        onWindowClosed: async () => {
            const result = await refetch();
            if (result.data && !result.error) {
                onReconnected?.();
            }
        },
    });

    return { isReconnectRequired, openSalesloftConnect };
};
